import { AnimatePresence, motion } from "framer-motion";
import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from "react";
import { GIVE_BACK_TEXT, GIVE_BACK_URL } from "../client-links";

export const DonateContext = createContext({ open: false, setOpen: () => {} } as {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
});

export const useDonate = () => {
  return useContext(DonateContext);
};

export const DonateContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const [open, setOpen] = useState(false);

  const context = useMemo(() => {
    return {
      open,
      setOpen,
    };
  }, [open, setOpen]);

  return <DonateContext.Provider value={context}>{children}</DonateContext.Provider>;
};

const variants = {
  active: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.15 },
  },
  inactive: {
    opacity: 0,
    y: -6,
    transition: { duration: 0.1 },
  },
};

const childVariants = {
  active: {
    opacity: 1,
    transition: { duration: 0.15 },
  },
  inactive: {
    opacity: 0,
    transition: { duration: 0.1 },
  },
};

export const Donate = () => {
  const { open } = useDonate();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="absolute right-0 top-full z-30 mt-4 w-[24rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-white border-opacity-20 bg-black text-left text-white shadow-2xl"
          variants={variants}
          initial="inactive"
          animate={open ? "active" : "inactive"}
        >
          <motion.div className="p-6" variants={childVariants}>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-primary">Support Firebird</p>
            <h2 className="mb-4 text-2xl font-bold uppercase">Give Back</h2>
            <p className="text-sm leading-6 text-gray-200">{GIVE_BACK_TEXT}</p>
          </motion.div>
          <motion.a
            className="group flex items-center justify-center gap-2 border-t border-white border-opacity-10 px-6 py-4 text-center text-sm font-bold uppercase tracking-[0.16em] transition hover:bg-white hover:text-black"
            href={GIVE_BACK_URL}
            target="_blank"
            rel="noreferrer"
            variants={childVariants}
          >
            <span>Give Back</span>
            <span className="-mt-0.5 text-lg leading-none transition group-hover:translate-x-1">→</span>
          </motion.a>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
